import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { useAccess, useAccountId } from "../Auth/auth-functions";
import { AzureADRoles } from "../Auth/auth-roles";
import { api } from "./api";
import { LocationDto, ListLocationDto, MutableLocationDto } from "../models/location.models";

export const useGetLocationsQuery = () => { 
	const hasAccess = useAccess(AzureADRoles.All);
	const accountId = useAccountId();

	return useQuery({
		queryKey: ['locations', accountId],
		queryFn: async () => {
			let res = await api.get<ListLocationDto[]>('/locations');
			return res.data;
		},
		staleTime: 5 * 60 * 1000,
		gcTime: 5 * 60 * 1000,
		enabled: hasAccess
	});
}

export const useGetLocationByIdQuery = (locationId: string | null) => {
	const hasAccess = useAccess(AzureADRoles.All);
	const accountId = useAccountId();

	return useQuery({
		queryKey: ['location', accountId, locationId],
		queryFn: async () => {
			let res = await api.get<LocationDto>(`/locations/${locationId}`);
			return res.data;
		},
		staleTime: 60 * 1000,
		gcTime: 5 * 60 * 1000,
		enabled: hasAccess && locationId !== "" && locationId !== "new" && locationId !== null
	});
}

export const useCreateLocationMutation = () => {
	const queryClient = useQueryClient();
	const accountId = useAccountId();

	return useMutation<AxiosResponse<LocationDto>, Error, MutableLocationDto>({
		mutationFn: (model) => {
			return api.post('/locations', model);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['locations', accountId] });
			queryClient.invalidateQueries({ queryKey: ['workorders', accountId] });
			queryClient.invalidateQueries({ queryKey: ['workorder', accountId] });
			queryClient.invalidateQueries({ queryKey: ['workorders-location', accountId] });
		}
	});
}

export const useUpdateLocationMutation = () => {
	const queryClient = useQueryClient();
	const accountId = useAccountId();

	return useMutation<AxiosResponse<LocationDto>, Error, MutableLocationDto>({
		mutationFn: (model) => {
			return api.put(`/locations/${model.LocationId}`, model);
		},
		onSuccess: (_data, model) => {
			queryClient.invalidateQueries({ queryKey: ['location', accountId, model.LocationId] });
			queryClient.invalidateQueries({ queryKey: ['locations', accountId] });
			queryClient.invalidateQueries({ queryKey: ['workorders', accountId] });
			queryClient.invalidateQueries({ queryKey: ['workorder', accountId] });
			queryClient.invalidateQueries({ queryKey: ['workorders-location', accountId] });

		}
	});
}

export const useDeleteLocationMutation = () => {
	const queryClient = useQueryClient();
	const accountId = useAccountId();

	return useMutation<unknown, Error, LocationDto>({
		mutationFn: (location) => {
			return api.delete(`/locations/${location.LocationId}`);
		},
		onSuccess: (_data, location) => {
			queryClient.invalidateQueries({ queryKey: ['location', accountId, location.LocationId] });
			queryClient.invalidateQueries({ queryKey: ['locations', accountId] });
			queryClient.invalidateQueries({ queryKey: ['workorders', accountId] });
			queryClient.invalidateQueries({ queryKey: ['workorder', accountId] });
			queryClient.invalidateQueries({ queryKey: ['workorders-location', accountId] });
		}
	});
}
